import React, { useEffect, useState } from 'react';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import { debounce } from 'lodash';
import { MockApolloClient } from 'mock-apollo-client';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

import ComboBox from '../../../ComboBox/ComboBox';
import { ComboBoxOption } from '../../../ComboBox/constants';
import { getCurrentUser } from '../../../../selectors/userSelectors';
import { ClientSummaryNode } from '../../../../generated/webapp_gql';
import { useClientSelectLazyQuery } from '../documents/ClientSelectQuery.generated';
import { getTherapyStatusMsg } from './helpers';

export interface Props {
  errorMessage?: string;
  // Note: the correct way to mock this would be to use the client from the GraphQL provider,
  // but this would also require either an API gateway/Federation, which we don't currently have.
  // In order to make this component testable, take client as a prop so we can mock gql data.
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
  onClientSelect: (selectedClient: ClientSummaryNode) => void;
}

const ClientSelect: React.FC<Props> = ({ errorMessage, memberClient, onClientSelect }) => {
  const [selectableClients, setSelectableClients] = useState<ComboBoxOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const currentUser = useSelector(getCurrentUser);
  const { showHpiClientStatusInfo } = useFlags();

  const providerIsPsychiatrist = currentUser.isPsychiatrist;

  const [getClientSearchResults, { data, error, loading }] = useClientSelectLazyQuery({
    client: memberClient,
    variables: {
      search: searchTerm,
    },
  });

  useEffect(() => {
    if (searchTerm && searchTerm.length > 0) {
      getClientSearchResults();
    }
  }, [getClientSearchResults, searchTerm]);

  useEffect(() => {
    if (loading) return;

    if (!error) {
      const edges = data?.clients?.edges ?? [];

      const clientOptions = edges.reduce((accumulator: ComboBoxOption[], edge) => {
        if (edge?.node && edge?.node.coachAssignment?.active) {
          const {
            companyName,
            isCompanyHpiEnabled,
            memberFirstName,
            memberInsuranceStatus,
            memberLastName,
            memberUuid,
            visitsRemaining,
          } = edge.node;

          let subText;

          if (
            showHpiClientStatusInfo &&
            currentUser.isTherapist &&
            visitsRemaining === 0 &&
            memberInsuranceStatus
          ) {
            subText =
              !providerIsPsychiatrist &&
              `${companyName} | ${getTherapyStatusMsg({
                isCompanyHpiEnabled: isCompanyHpiEnabled || false,
                memberInsuranceStatus,
              })}`;
          } else {
            const contextOfVisitsDisplayText = currentUser.isTherapist ? 'therapy' : 'coaching';
            subText =
              !providerIsPsychiatrist &&
              `${companyName} | ${visitsRemaining} ${contextOfVisitsDisplayText}`;
          }

          return [
            ...accumulator,
            {
              displayText: `${memberFirstName} ${memberLastName}`,
              key: memberUuid,
              subText,
              value: memberUuid,
            },
          ];
        }

        return accumulator;
      }, []);

      setSelectableClients(clientOptions);
    }
  }, [
    currentUser.isTherapist,
    data,
    error,
    loading,
    setSelectableClients,
    showHpiClientStatusInfo,
  ]);

  const handleInputChange = (newSearchTerm: string) => {
    debounce(() => setSearchTerm(newSearchTerm ?? ''), 500, {
      leading: true,
    })();
  };

  const handleOnSelect = ({ value }: ComboBoxOption) => {
    const selectedClient = data?.clients?.edges?.find(
      (edge) => value === edge?.node?.memberUuid,
    )?.node;

    if (selectedClient) {
      onClientSelect(selectedClient);
    }
  };

  return (
    <div>
      <ComboBox
        errorMessage={errorMessage}
        loading={loading}
        loadingMessage="Finding matching clients..."
        onInputChange={handleInputChange}
        onSelect={handleOnSelect}
        placeholder="Search for a client"
        selectableOptions={selectableClients}
      />
    </div>
  );
};

export default ClientSelect;
